import { exerciseKeyOf } from "./repEstimator";
import { isCountedUnit, isRepUnit } from "./planNodeUtils";

export const createPhaseLogEntry = (node, actualDurationSec, extra = {}) => {
  if (!node?.id) return null;

  const duration = Math.max(0, Math.round(Number(actualDurationSec) || 0));
  const count = Number(node.value);
  const counted =
    isCountedUnit(node.unit) && Number.isFinite(count) && count > 0;

  return {
    nodeId: node.id,
    nodeLabel: node.label,
    exerciseKey: exerciseKeyOf(node),
    // `repCount` stays REPS-ONLY. It is the historical field name and the
    // history aggregator divides duration by it to get seconds-per-rep — a
    // calorie count landing here would be read back as a rep pace. Calorie
    // targets travel in countTarget/countUnit instead, and the aggregator
    // buckets them under their own key namespace (see calHistoryKey).
    repCount: counted && isRepUnit(node.unit) ? count : null,
    countTarget: counted ? count : null,
    countUnit: counted ? String(node.unit).toLowerCase() : null,
    actualDurationSec: duration,
    ...extra,
  };
};

/**
 * Log entries for a finished phase: one, or one per step for linked steps
 * finished on one Done (#177), with the time split by each step's estimate.
 */
export const createPhaseLogEntries = (node, actualDurationSec, extra = {}) => {
  const linked = node?.linkedNodes;
  if (!Array.isArray(linked) || linked.length < 2) {
    const entry = createPhaseLogEntry(node, actualDurationSec, extra);
    return entry ? [entry] : [];
  }
  const shares = Array.isArray(node.linkedShares) ? node.linkedShares : [];
  const total = shares.reduce((a, b) => a + (Number(b) || 0), 0);
  const actual = Math.max(0, Number(actualDurationSec) || 0);
  return linked
    .map((n, i) => {
      const part = total > 0 ? (Number(shares[i]) || 0) / total : 1 / linked.length;
      return createPhaseLogEntry(n, actual * part, extra);
    })
    .filter(Boolean);
};

// A set counts as done when time was actually spent on it. Until #92 phase 2
// the Coach's Done button logged reps sets as skipped, so the skip flag can't
// be trusted for older sessions; a near-zero duration means it was skipped
// past.
export const MIN_SET_SECONDS = 5;

/**
 * The reps sets done, in log order (#97, #144): every entry with a rep target
 * that time was spent on, other than one cut off by the session ending.
 * `index` is the entry's place in the log (what amendReps takes), `setNumber`
 * counts that exercise's entries so far, and `actual` is the corrected figure
 * if there is one, otherwise the prescription.
 */
export const doneRepsSets = (log = []) => {
  const seen = {};
  const sets = [];
  (log || []).forEach((entry, index) => {
    if (!entry?.nodeId) return;
    seen[entry.nodeId] = (seen[entry.nodeId] || 0) + 1;
    if (entry.repCount == null) return;
    if (!(Number(entry.actualDurationSec) >= MIN_SET_SECONDS)) return;
    if (entry.skipReason === "sessionEnded") return;
    sets.push({
      index,
      nodeId: entry.nodeId,
      label: entry.nodeLabel || "Set",
      prescribed: entry.repCount,
      actual: entry.actualReps ?? entry.repCount,
      setNumber: seen[entry.nodeId],
    });
  });
  return sets;
};

/**
 * The set just finished, offered for correction during the rest after it:
 * the last reps entry, or one just behind the rest or changeover that
 * followed it. Null when that set was skipped past, rather than reaching
 * back to an older one.
 */
export const lastRepsSet = (log = []) => {
  const entries = log || [];
  for (let i = entries.length - 1; i >= Math.max(0, entries.length - 3); i--) {
    if (entries[i]?.repCount == null) continue;
    return doneRepsSets(entries).find((set) => set.index === i) || null;
  }
  return null;
};

/**
 * Record the reps actually done on one set, in place: the log is a ref's
 * array that the running session keeps appending to. `repCount` stays the
 * prescription, because rep-pace history divides the set's duration by it.
 * Kept even when it matches the prescription: a correction is the user
 * confirming the figure, which progression reads as reps known.
 */
export const amendReps = (log, index, reps) => {
  const entry = log?.[index];
  const n = Math.round(Number(reps));
  if (!entry || entry.repCount == null || !Number.isFinite(n) || n < 0 || n > 999) return false;
  log[index] = { ...entry, actualReps: n };
  return true;
};

/**
 * Reps done per set for each Work node id, but only for exercises where some
 * set's reps differ from the prescription: the figures worth showing next to
 * a prescription that would otherwise read as done to the letter.
 */
export const correctedRepsById = (log = []) => {
  const byId = {};
  for (const set of doneRepsSets(log)) (byId[set.nodeId] ||= []).push(set);
  const out = {};
  for (const [id, sets] of Object.entries(byId)) {
    if (sets.some((set) => set.actual !== set.prescribed)) out[id] = sets.map((set) => set.actual);
  }
  return out;
};

export const buildPhaseStatsMap = (phaseLog = []) => {
  const map = {};

  for (const entry of phaseLog) {
    if (!entry?.nodeId || entry.actualDurationSec == null) continue;

    if (!map[entry.nodeId]) {
      map[entry.nodeId] = {
        nodeId: entry.nodeId,
        nodeLabel: entry.nodeLabel,
        durations: [],
        totalDurationSec: 0,
        averageDurationSec: 0,
        stdDevSec: 0,
        minDurationSec: 0,
        maxDurationSec: 0,
        uniform: false,
        attemptCount: 0,
      };
    }

    const duration = Math.max(
      0,
      Math.round(Number(entry.actualDurationSec) || 0),
    );
    map[entry.nodeId].durations.push(duration);
    map[entry.nodeId].totalDurationSec += duration;
    map[entry.nodeId].attemptCount += 1;
  }

  for (const stats of Object.values(map)) {
    stats.averageDurationSec = stats.attemptCount
      ? Math.round(stats.totalDurationSec / stats.attemptCount)
      : 0;
    stats.minDurationSec = Math.min(...stats.durations);
    stats.maxDurationSec = Math.max(...stats.durations);
    // Every split landed on the same second. A timed phase ends on the clock,
    // so a Tabata's 32 work intervals all read 0:20 — listing them one by one
    // says nothing a count and a single figure don't (issue #82).
    stats.uniform =
      stats.attemptCount >= 2 && stats.minDurationSec === stats.maxDurationSec;
    // Population standard deviation of the recorded splits, rounded to whole
    // seconds. Only meaningful for a looped phase (≥2 attempts); 0 otherwise.
    if (stats.attemptCount >= 2) {
      const mean = stats.totalDurationSec / stats.attemptCount;
      const variance =
        stats.durations.reduce((acc, d) => acc + (d - mean) ** 2, 0) /
        stats.attemptCount;
      stats.stdDevSec = Math.round(Math.sqrt(variance));
    }
  }

  return map;
};

export const buildAverageDurationMap = (phaseLog = []) => {
  const statsMap = buildPhaseStatsMap(phaseLog);
  const averageMap = {};

  for (const [nodeId, stats] of Object.entries(statsMap)) {
    averageMap[nodeId] = stats.averageDurationSec;
  }

  return averageMap;
};

export const formatPhaseDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const formatPhaseSummary = (stats) => {
  if (!stats) return "";
  const single = stats.attemptCount <= 1;
  const seconds = single ? stats.totalDurationSec : stats.averageDurationSec;
  // Nothing worth reporting was recorded. "0:00" is a claim — that the phase
  // took no time — where the truth is that there is no figure at all. Every
  // caller already treats "" as "print nothing" (SessionPlanOutline guards on
  // it), so say nothing rather than something false. Rounded, because a phase
  // logged at 0.4s would otherwise still render as 0:00.
  if (!(Math.round(Number(seconds) || 0) > 0)) return "";
  const text = formatPhaseDuration(seconds);
  if (single) return text;
  if (stats.uniform === true) return `${stats.attemptCount} × ${text}`;
  return stats.stdDevSec > 0 ? `avg ${text} ±${stats.stdDevSec}s` : `avg ${text}`;
};

/**
 * Split a phase's recorded durations into one row per outer round, so a
 * Tabata run as 4 sets of 8 reads as four rows of eight rather than one
 * wrapped run of 32 (issue #82). `outerRounds` is how many times the phase's
 * enclosing block was itself repeated.
 *
 * The phase log carries no round index, so rows are inferred by count. When
 * the splits don't divide evenly — a session stopped part-way, say —
 * inventing a boundary would put splits in the wrong set, so the whole list
 * comes back as a single row instead.
 */
export const chunkSplits = (durations = [], outerRounds = 1) => {
  const rounds = Math.floor(Number(outerRounds) || 0);
  const n = durations.length;
  if (n === 0) return [];
  if (rounds < 2 || n % rounds !== 0 || n / rounds < 2) return [durations];
  const size = n / rounds;
  const rows = [];
  for (let i = 0; i < n; i += size) rows.push(durations.slice(i, i + size));
  return rows;
};
